import { BaseApiClient } from '../base-client'
import {
  API_ROUTES,
  ApiResponse,
  ApiKeyCredentialIntegrationType,
  interpolateRoute,
  type HubspotAssignee,
  type HubspotMetadata,
  type HubspotTicketPipeline,
} from '@super/shared-types'
import { withErrorHandling } from '../../utils'

export type { HubspotAssignee, HubspotMetadata, HubspotTicketPipeline }

/**
 * HubSpot Integration API Client
 * Connect (OAuth), assignee lookups, and integration-level settings.
 */
export class HubspotClient extends BaseApiClient {
  /**
   * Get install URL for HubSpot (initiates OAuth flow).
   * @param returnPath - optional same-origin path (e.g. `/onboarding/integrations`)
   *   the backend should redirect to after the OAuth callback finishes. If
   *   omitted, the callback falls back to `/settings/integrations`.
   */
  getInstallUrl(returnPath?: string): string {
    const base = `${this.baseUrl}${API_ROUTES.INTEGRATIONS.HUBSPOT.INSTALL}`
    return returnPath
      ? `${base}?return=${encodeURIComponent(returnPath)}`
      : base
  }

  /**
   * [ADMIN] Get install URL for HubSpot for a specific company
   */
  getAdminInstallUrl(companyId: string): string {
    return `${this.baseUrl}${API_ROUTES.ADMIN.INTEGRATIONS.HUBSPOT.INSTALL}?companyId=${companyId}`
  }

  /**
   * Update HubSpot integration metadata (e.g. default task assignee).
   * Uses the generic API key credentials update endpoint with metadata only.
   */
  async updateMetadata(metadata: HubspotMetadata): Promise<void> {
    const url = interpolateRoute(
      API_ROUTES.INTEGRATIONS.API_KEY_CREDENTIALS.UPDATE,
      { integrationType: ApiKeyCredentialIntegrationType.HUBSPOT },
    )
    await withErrorHandling(
      async () => {
        return await this.request<ApiResponse<{ message: string }>>(url, {
          method: 'PUT',
          body: JSON.stringify({ metadata }),
        })
      },
      { errorContext: 'Error updating HubSpot metadata' },
    )
  }

  /**
   * [ADMIN] Update HubSpot integration metadata for a specific company.
   */
  async updateAdminMetadata(
    companyId: string,
    metadata: HubspotMetadata,
  ): Promise<void> {
    const url = interpolateRoute(
      API_ROUTES.ADMIN.INTEGRATIONS.API_KEY_CREDENTIALS.UPDATE,
      { integrationType: ApiKeyCredentialIntegrationType.HUBSPOT },
    )
    await withErrorHandling(
      async () => {
        return await this.request<ApiResponse<{ message: string }>>(
          `${url}?companyId=${companyId}`,
          { method: 'PUT', body: JSON.stringify({ metadata }) },
        )
      },
      { errorContext: 'Error updating admin HubSpot metadata' },
    )
  }

  /**
   * Get task assignees (HubSpot users) for the current user's company
   */
  async getAssignees(): Promise<HubspotAssignee[]> {
    const result = await withErrorHandling(
      async () => {
        const response = await this.request<ApiResponse<HubspotAssignee[]>>(
          API_ROUTES.INTEGRATIONS.HUBSPOT.ASSIGNEES,
        )
        return response.data ?? []
      },
      { errorContext: 'Error fetching HubSpot assignees' },
    )
    return result ?? []
  }

  /**
   * [ADMIN] Get task assignees (HubSpot users) for a specific company
   */
  async getAdminAssignees(companyId: string): Promise<HubspotAssignee[]> {
    const result = await withErrorHandling(
      async () => {
        const response = await this.request<ApiResponse<HubspotAssignee[]>>(
          `${API_ROUTES.ADMIN.INTEGRATIONS.HUBSPOT.ASSIGNEES}?companyId=${companyId}`,
        )
        return response.data ?? []
      },
      { errorContext: 'Error fetching admin HubSpot assignees' },
    )
    return result ?? []
  }

  /**
   * Get ticket pipelines (with stages) for the current user's company
   */
  async getTicketPipelines(): Promise<HubspotTicketPipeline[]> {
    const result = await withErrorHandling(
      async () => {
        const response = await this.request<
          ApiResponse<HubspotTicketPipeline[]>
        >(API_ROUTES.INTEGRATIONS.HUBSPOT.TICKET_PIPELINES)
        return response.data ?? []
      },
      { errorContext: 'Error fetching HubSpot ticket pipelines' },
    )
    return result ?? []
  }

  /**
   * [ADMIN] Get ticket pipelines (with stages) for a specific company
   */
  async getAdminTicketPipelines(
    companyId: string,
  ): Promise<HubspotTicketPipeline[]> {
    const result = await withErrorHandling(
      async () => {
        const response = await this.request<
          ApiResponse<HubspotTicketPipeline[]>
        >(
          `${API_ROUTES.ADMIN.INTEGRATIONS.HUBSPOT.TICKET_PIPELINES}?companyId=${companyId}`,
        )
        return response.data ?? []
      },
      { errorContext: 'Error fetching admin HubSpot ticket pipelines' },
    )
    return result ?? []
  }

  /**
   * Disconnect HubSpot integration
   * Uses the generic API key credentials disconnect endpoint
   */
  async disconnect(): Promise<void> {
    const url = interpolateRoute(
      API_ROUTES.INTEGRATIONS.API_KEY_CREDENTIALS.DELETE,
      { integrationType: ApiKeyCredentialIntegrationType.HUBSPOT },
    )
    await withErrorHandling(
      async () => {
        return await this.request<ApiResponse<{ message: string }>>(url, {
          method: 'DELETE',
        })
      },
      { errorContext: 'Error disconnecting HubSpot integration' },
    )
  }

  /**
   * [ADMIN] Disconnect HubSpot integration for a specific company
   */
  async disconnectAdmin(companyId: string): Promise<void> {
    const url = interpolateRoute(
      API_ROUTES.ADMIN.INTEGRATIONS.API_KEY_CREDENTIALS.DELETE,
      { integrationType: ApiKeyCredentialIntegrationType.HUBSPOT },
    )
    await withErrorHandling(
      async () => {
        return await this.request<ApiResponse<{ message: string }>>(
          `${url}?companyId=${companyId}`,
          { method: 'DELETE' },
        )
      },
      { errorContext: 'Error disconnecting admin HubSpot integration' },
    )
  }
}

// Export singleton instance
export const hubspotClient = new HubspotClient()
